/**
 * Sitzungskontext: stellt API-Client, Benutzerprofil und Berechtigungen bereit.
 * Die hier bekannten Berechtigungen steuern ausschliesslich die Darstellung;
 * durchgesetzt werden sie serverseitig bei jedem Aufruf.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { ApiClient, ApiError } from './apiClient.js';
import { api } from '../api/endpoints.js';
import type { MeResponse, Permission } from '../api/types.js';

export interface SessionContextValue {
  client: ApiClient;
  profile: MeResponse | null;
  loading: boolean;
  error: ApiError | null;
  has: (permission: Permission) => boolean;
  reload: () => void;
  signOut: () => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0]);
  const [profile, setProfile] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const client = useMemo(() => {
    if (!account) return null;
    return new ApiClient({
      instance,
      account,
      onSessionExpired: () => setProfile(null),
    });
  }, [instance, account]);

  useEffect(() => {
    if (!client) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    api
      .me(client, controller.signal)
      .then((me) => {
        if (!controller.signal.aborted) setProfile(me);
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        setError(e instanceof ApiError ? e : new ApiError(500, 'internal_error', String(e)));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [client, reloadToken]);

  const value = useMemo<SessionContextValue | undefined>(() => {
    if (!client) return undefined;
    const granted = new Set<Permission>(profile?.permissions ?? []);
    return {
      client,
      profile,
      loading,
      error,
      has: (permission) => granted.has(permission),
      reload: () => setReloadToken((n) => n + 1),
      signOut: () => {
        void instance.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin });
      },
    };
  }, [client, profile, loading, error, instance, account]);

  if (!value) return null;
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession muss innerhalb von <SessionProvider> verwendet werden.');
  return ctx;
}
